import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../lib/authedFetch';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Trash2,
  Filter,
  ExternalLink,
  Award,
  TrendingUp,
  TrendingDown,
  GitCompare,
  AlertCircle,
  Lightbulb,
  Loader2,
  Inbox,
  ChevronRight,
  X,
  MoreHorizontal
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { fmtOrg, tzSuffix } from '../lib/orgTime';
import { useOrgTz } from '../hooks/useOrgTz';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  'award': Award,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'git-compare': GitCompare,
  'alert-circle': AlertCircle,
  'lightbulb': Lightbulb,
  'bell': Bell
};

const COLOR_MAP = {
  'emerald': { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700' },
  'blue': { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
  'red': { bg: 'bg-rose-50', border: 'border-rose-200', icon: 'text-rose-600', badge: 'bg-rose-100 text-rose-700' },
  'purple': { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700' },
  'amber': { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' },
  'indigo': { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600', badge: 'bg-indigo-100 text-indigo-700' },
  'slate': { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-600', badge: 'bg-slate-100 text-slate-700' }
};

const PRIORITY_BADGES = {
  'high': { color: 'bg-rose-100 text-rose-700 border-rose-200', label: 'High' },
  'medium': { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Medium' },
  'low': { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Low' }
};

function NotificationCard({ notification, onMarkRead, onDismiss, onNavigate }) {
  const tz = useOrgTz();
  const IconComponent = ICON_MAP[notification.icon] || Bell;
  const colors = COLOR_MAP[notification.color] || COLOR_MAP.slate;
  const priority = PRIORITY_BADGES[notification.priority] || PRIORITY_BADGES.medium;
  
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return `${fmtOrg(date, tz, { day: 'numeric', month: 'short', year: 'numeric' })}${tzSuffix(tz)}`;
  };
  
  return (
    <div 
      className={`p-4 rounded-lg border-2 transition-all ${
        notification.read 
          ? 'bg-white border-slate-100 opacity-70' 
          : `${colors.bg} ${colors.border}`
      }`}
      data-testid={`notification-${notification.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`p-2 rounded-lg ${notification.read ? 'bg-slate-100' : colors.bg}`}>
          <IconComponent className={`h-5 w-5 ${notification.read ? 'text-slate-400' : colors.icon}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`font-medium ${notification.read ? 'text-slate-500' : 'text-slate-900'}`}>
              {notification.title}
            </h4>
            {!notification.read && (
              <div className="w-2 h-2 rounded-full bg-indigo-500" />
            )}
            <Badge variant="outline" className={`text-xs ${priority.color}`}>
              {priority.label}
            </Badge>
          </div>
          <p className={`text-sm ${notification.read ? 'text-slate-400' : 'text-slate-600'}`}>
            {notification.message}
          </p>
          <p className="text-xs text-slate-400 mt-2"><span title={`${fmtOrg(notification.created_at, tz)}${tzSuffix(tz)}`} data-testid="notification-time">{timeAgo(notification.created_at)}</span></p>
        </div>
        
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMarkRead(notification.id, !notification.read)}
            title={notification.read ? 'Mark as unread' : 'Mark as read'}
            className="h-8 w-8 p-0"
          >
            {notification.read ? (
              <Bell className="h-4 w-4 text-slate-400" />
            ) : (
              <Check className="h-4 w-4 text-indigo-600" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate(notification.source_route)}
            title="Go to source"
            className="h-8 w-8 p-0"
          >
            <ExternalLink className="h-4 w-4 text-slate-400 hover:text-indigo-600" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDismiss(notification.id)}
            title="Dismiss"
            className="h-8 w-8 p-0 text-slate-400 hover:text-rose-500"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function NotificationCenterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [types, setTypes] = useState([]);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const fetchNotifications = useCallback(async (reset = false) => {
    try {
      const skip = reset ? 0 : page * LIMIT;
      const params = new URLSearchParams({ skip: skip.toString(), limit: LIMIT.toString() });
      
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (showUnreadOnly) params.append('unread_only', 'true');
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      
      const response = await authedFetch(`${API_URL}/api/notifications?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (reset) {
          setNotifications(data.notifications);
        } else {
          setNotifications(prev => [...prev, ...data.notifications]);
        }
        setTotal(data.total);
        setUnreadCount(data.unread_count);
        setHasMore(data.has_more);
      }
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    }
    setLoading(false);
  }, [page, typeFilter, showUnreadOnly, priorityFilter]);

  const fetchTypes = useCallback(async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/notifications/types`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTypes(data.types);
      }
    } catch (error) {
      logger.error('Error fetching types:', error);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setPage(0);
    fetchNotifications(true);
  }, [typeFilter, showUnreadOnly, priorityFilter, fetchNotifications]);

  useEffect(() => {
    if (page > 0) {
      fetchNotifications(false);
    }
  }, [page, fetchNotifications]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const markRead = async (notificationId, read) => {
    try {
      const response = await authedFetch(`${API_URL}/api/notifications/${notificationId}/read?read=${read}`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (response.ok) {
        setNotifications(prev => prev.map(n => 
          n.id === notificationId ? { ...n, read } : n
        ));
        setUnreadCount(prev => read ? prev - 1 : prev + 1);
      }
    } catch (error) {
      logger.error('Error marking notification:', error);
    }
  };

  const markAllRead = async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      logger.error('Error marking all as read:', error);
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      const response = await authedFetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const dismissed = notifications.find(n => n.id === notificationId);
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setTotal(prev => prev - 1);
        if (!dismissed?.read) {
          setUnreadCount(prev => prev - 1);
        }
        toast.success('Notification dismissed');
      }
    } catch (error) {
      logger.error('Error dismissing notification:', error);
    }
  };

  const dismissAll = async (readOnly = false) => {
    try {
      const response = await authedFetch(`${API_URL}/api/notifications/dismiss-all?read_only=${readOnly}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.dismissed_count} notifications dismissed`);
        fetchNotifications(true);
      }
    } catch (error) {
      logger.error('Error dismissing all:', error);
    }
  };

  const handleNavigate = (route) => {
    navigate(route);
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Reengage');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`Notifications`} description={`Your inbox of system and alert notifications.`} theme={__pageTheme} />
    )}>
    <div className="max-w-4xl mx-auto space-y-6" data-testid="notification-center-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={markAllRead}
            disabled={unreadCount === 0}
          >
            <CheckCheck className="h-4 w-4 mr-2" />
            Mark All Read
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="h-4 w-4 mr-2" />
                Dismiss
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => dismissAll(true)}>
                Dismiss read notifications
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => dismissAll(false)} className="text-rose-600">
                Dismiss all notifications
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="type-filter">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {types.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[140px]" data-testid="priority-filter">
                <SelectValue placeholder="All priorities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-2">
              <Switch
                id="unread-only"
                checked={showUnreadOnly}
                onCheckedChange={setShowUnreadOnly}
              />
              <Label htmlFor="unread-only" className="text-sm cursor-pointer">
                Unread only
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      {notifications.length > 0 ? (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkRead={markRead}
              onDismiss={dismissNotification}
              onNavigate={handleNavigate}
            />
          ))}
          
          {hasMore && (
            <div className="text-center pt-4">
              <Button
                variant="outline"
                onClick={() => setPage(prev => prev + 1)}
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ChevronRight className="h-4 w-4 mr-2" />
                )}
                Load More
              </Button>
            </div>
          )}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Inbox className="h-16 w-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No notifications</h3>
            <p className="text-slate-500 mt-2">
              {showUnreadOnly || typeFilter !== 'all' || priorityFilter !== 'all'
                ? "No notifications match your filters"
                : "You're all caught up!"
              }
            </p>
            {(showUnreadOnly || typeFilter !== 'all' || priorityFilter !== 'all') && (
              <Button
                variant="link"
                onClick={() => {
                  setTypeFilter('all');
                  setPriorityFilter('all');
                  setShowUnreadOnly(false);
                }}
                className="mt-4"
              >
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
    </PageShell>
  );
}
